import { TemplateRow } from 'core';
import { Bid, BidFullYearSource, BidStatus, TRACKER_FREQUENCIES, TrackerFrequency, VendorQuoteBasis } from '../../_models/bid';
import { SamplePlanCountry, SamplePlanSpec } from './bid-spec-parsing';
import { BidServicesBidsState, BidServicesState } from './bid-draft.service';

export interface BidTrackerFields {
  isTracker: boolean;
  trackerFrequency: TrackerFrequency | null;
  firstWaveFieldStart: string | null;
  trackerTag: string | null;
}

export const WAVES_PER_YEAR: Record<TrackerFrequency, number> = {
  Monthly: 12,
  Quarterly: 4,
  'Bi-Annual': 2,
  Yearly: 1,
};

function asFrequency(value: string | null | undefined): TrackerFrequency | null {
  return TRACKER_FREQUENCIES.find(f => f === value) ?? null;
}

/** The API doesn't store the bid-level tracker fields yet, so a reloaded bid falls back to the sample
 *  plan's per-audience flags (which bids marked on the old Sample Specs dropdown also carry). */
export function trackerFieldsFromBid(bid: Partial<Bid> | null | undefined): BidTrackerFields {
  const entry = (bid?.samplePlan ?? []).flatMap(c => c.audiences ?? []).find(a => a.isTracker);
  const isTracker = bid?.isTracker ?? !!entry;
  return {
    isTracker,
    trackerFrequency: isTracker ? asFrequency(bid?.trackerFrequency ?? entry?.trackerFrequency) : null,
    firstWaveFieldStart: isTracker ? (bid?.firstWaveFieldStart ?? null) : null,
    trackerTag: isTracker ? (bid?.trackerTag ?? null) : null,
  };
}

export function isTrackerBid(bid: Bid): boolean {
  return trackerFieldsFromBid(bid).isTracker;
}

/** Trackers renew with the calendar year, so from 15 December to 15 January a tracker's clone is its
 *  renewal: the new year can't be started as another wave. */
export function inRenewalWindow(today: Date = new Date()): boolean {
  const month = today.getMonth();
  const day = today.getDate();
  return (month === 11 && day >= 15) || (month === 0 && day <= 15);
}

/** What a tracker's clone starts as: usually its next wave, its renewal in the renewal window. */
export function defaultTrackerCloneType(): 'wave' | 'renewal' {
  return inRenewalWindow() ? 'renewal' : 'wave';
}

/** What a bid is to its tracker (e.g. "Quarterly, next wave"), or null for a one-off bid. */
export function trackerKindLabel(bid: Bid | null | undefined): string | null {
  const frequency = trackerFieldsFromBid(bid).trackerFrequency;
  if (!frequency) return null;
  const kind = bid?.trackerCloneType === 'wave' ? 'next wave'
    : bid?.trackerCloneType === 'renewal' ? 'renewal' : 'new tracker';
  return `${frequency}, ${kind}`;
}

/** What the read-only Tracker ID field shows, or null to hide it. A won tracker always shows the
 *  field, with a placeholder until the API returns its generated ID. */
export function trackerIdLabel(bid: Bid | null | undefined): string | null {
  if (bid?.trackerId) return bid.trackerId;
  return bid?.status === BidStatus.Sold && trackerFieldsFromBid(bid).isTracker ? 'Not assigned yet' : null;
}

const TRACKER_WORDING = /\btrack(?:er|ing study)\b|\bwaves?\b/gi;
// Bi-Annual is tested before Yearly so "bi-annual" never reads as annual; "bi-monthly" has no
// matching option, so it isn't read as Monthly either.
const FREQUENCY_WORDING: [TrackerFrequency, RegExp][] = [
  ['Monthly', /(?<!bi-?)\bmonthly\b|\b(?:every|each|per) month\b|\b12 waves\b/i],
  ['Quarterly', /\bquarterly\b|\b(?:every|each|per) quarter\b|\b4 waves\b/i],
  ['Bi-Annual', /\b(?:bi|semi)-?annual(?:ly)?\b|\btwice a year\b|\bevery (?:6|six) months\b|\b2 waves\b/i],
  ['Yearly', /\byearly\b|(?<!(?:bi|semi)-?)\bannual(?:ly)?\b|\bonce a year\b|\b(?:every|each) year\b/i],
];

/**
 * The tracker frequency a pasted request describes, or null when it doesn't describe a tracker or
 * names more than one frequency. The parser doesn't detect trackers yet, so this reads the text:
 * a frequency only counts near tracker wording, so "annual income" in a screener isn't a tracker.
 */
export function trackerFrequencyFromText(text: string): TrackerFrequency | null {
  const found = new Set<TrackerFrequency>();
  for (const m of text.matchAll(TRACKER_WORDING)) {
    const near = text.slice(Math.max(0, m.index - 60), m.index + m[0].length + 60);
    for (const [frequency, pattern] of FREQUENCY_WORDING) {
      if (pattern.test(near)) found.add(frequency);
    }
  }
  return found.size === 1 ? [...found][0] : null;
}

/** The tracker frequency a parse found: the parser's own answer when it gives one, else the text's. */
export function trackerFrequencyFromParse(plan: SamplePlanCountry[], text: string): TrackerFrequency | null {
  const parsed = plan.flatMap(c => c.audiences ?? []).find(a => a.isTracker && asFrequency(a.trackerFrequency));
  return asFrequency(parsed?.trackerFrequency) ?? trackerFrequencyFromText(text);
}

/** Writes the bid-level tracker choice onto every sample plan audience, which is what the Gut Check
 *  feasibility call and the API currently read. */
export function applyTrackerToSamplePlan(plan: SamplePlanCountry[] | null | undefined, isTracker: boolean, frequency: TrackerFrequency | null): SamplePlanCountry[] | null {
  if (!plan) return plan ?? null;
  return plan.map(c => ({
    ...c,
    audiences: (c.audiences ?? []).map(a => ({ ...a, isTracker, trackerFrequency: isTracker ? frequency : null })),
  }));
}

/** Why a tracker bid can't move to `status` yet, or null when it can. Takes a list row as well as a
 *  full bid, since it reads only the tracker fields, status and Sent to Client date. */
export function trackerStatusBlocker(bid: Partial<Bid> | null | undefined, status: BidStatus): string | null {
  if (status !== BidStatus.SentToClient && status !== BidStatus.Sold) return null;
  const t = trackerFieldsFromBid(bid);
  if (!t.isTracker) return null;
  const label = status === BidStatus.Sold ? 'Closed Won' : 'Sent to Client';
  // No date checks: the Closed Won dialog asks for the field dates, and the API saves the First Wave
  // Field Start and a wave's Start and End Date from them.
  // A renewal reprices the year, so the client must see it; unlike a wave it can't skip Sent to Client.
  const renewalUnsent = status === BidStatus.Sold && bid?.trackerCloneType === 'renewal'
    && bid.status !== BidStatus.SentToClient && !bid.sentToClientAt;
  const renewalMessage = 'A renewal must be Sent to Client before it can be marked Closed Won.';
  if (t.trackerFrequency) return renewalUnsent ? renewalMessage : null;
  const message = `This tracker needs a Frequency on Proposal Details before it can be marked ${label}.`;
  return renewalUnsent ? `${message} ${renewalMessage}` : message;
}

/** Whether the bid keeps a Full-Year Pricing copy when sent: any tracker except a cloned wave, which is
 *  already priced for one wave. */
export function takesFullYearPricing(bid: Bid | null | undefined): boolean {
  return trackerFieldsFromBid(bid).isTracker && bid?.trackerCloneType !== 'wave';
}

/** A wave's share of a full-year quantity, kept to cents. */
export function waveShare(n: number, waves: number): number {
  return Math.round((n / waves) * 100) / 100;
}

/** A wave's share of a full-year completes figure: whole completes, at least one. */
export function waveCompletes(n: number, waves: number): number {
  return Math.max(1, Math.round(n / waves));
}

/** How many waves a full-year tracker bid's quantities divide into, or null when the bid is already one
 *  wave: not a tracker, a Yearly tracker, or a cloned wave (copied from a wave bid). */
export function fullYearWaves(bid: Bid | null | undefined): number | null {
  const frequency = trackerFieldsFromBid(bid).trackerFrequency;
  const waves = frequency ? WAVES_PER_YEAR[frequency] : 1;
  return waves > 1 && bid?.trackerCloneType !== 'wave' ? waves : null;
}

/** One wave's share of a sample size, as vendors are asked to quote it and wave 1 books it. */
export function waveSampleSize(n: number, waves: number | null): number {
  return waves && n > 0 ? waveCompletes(n, waves) : n;
}

/** Whether the bid's vendors quote the full year. Only a full-year tracker can; anything else quotes
 *  one wave whatever is stored. */
export function vendorQuotesFullYear(bid: Bid | null | undefined, basis: VendorQuoteBasis | null | undefined = bid?.vendorQuoteBasis): boolean {
  return basis === 'year' && fullYearWaves(bid) != null;
}

/** How many waves the vendor's sample sizes divide into, or null when the vendor sees the bid's own
 *  figures (not a full-year tracker, or vendors quote the full year). */
export function vendorWaves(bid: Bid | null | undefined, basis: VendorQuoteBasis | null | undefined = bid?.vendorQuoteBasis): number | null {
  return vendorQuotesFullYear(bid, basis) ? null : fullYearWaves(bid);
}

/** Tells a vendor the bid is a tracker and what its sample sizes cover, or null for a one-off bid. */
export function vendorTrackerNote(bid: Bid | null | undefined, basis: VendorQuoteBasis | null | undefined = bid?.vendorQuoteBasis): string | null {
  const frequency = trackerFieldsFromBid(bid).trackerFrequency;
  if (!frequency) return null;
  const waves = WAVES_PER_YEAR[frequency];
  const covers = vendorQuotesFullYear(bid, basis) ? 'the full year' : 'one wave';
  return `${frequency} tracker, ${waves} ${waves === 1 ? 'wave' : 'waves'} a year. Sample sizes and feasibility are for ${covers}.`;
}

/**
 * Cuts the confirmed full year down to the option(s) the client bought, so a renewal reopens it as
 * stored. Removing an option shifts the spec indexes, and a Multi-Audience plan cut to one audience
 * switches to country keys, so the kept commitments and vendor lines are rekeyed to the smaller plan.
 * With nothing marked sold, everything is kept.
 */
export function soldFullYearSource(full: BidFullYearSource, soldKeys: string[]): BidFullYearSource {
  const { plan, keyMap } = soldOnlyPlan(full.samplePlan, new Set(soldKeys));
  const rekey = <T extends { key: string }>(rows: T[]): T[] =>
    rows.flatMap(r => keyMap.has(r.key) ? [{ ...r, key: keyMap.get(r.key)! }] : []);
  return { ...full, samplePlan: plan, commitments: rekey(full.commitments), vendorScenarios: rekey(full.vendorScenarios) };
}

/** Keeps only the sold specs, dropping any audience or country left with none, and maps each kept
 *  scenario key to its key in the smaller plan. */
function soldOnlyPlan(plan: SamplePlanCountry[], sold: Set<string>): { plan: SamplePlanCountry[]; keyMap: Map<string, string> } {
  const oldKeys = specKeys(plan);
  if (!sold.size) return { plan, keyMap: new Map([...oldKeys.values()].map(k => [k, k])) };
  const kept = plan
    .map(c => ({
      ...c,
      audiences: c.audiences
        .map(a => ({ ...a, specs: a.specs.filter(s => sold.has(oldKeys.get(s) ?? '')) }))
        .filter(a => a.specs.length > 0),
    }))
    .filter(c => c.audiences.length > 0);
  const keyMap = new Map<string, string>();
  for (const [spec, key] of specKeys(kept)) {
    const oldKey = oldKeys.get(spec);
    if (oldKey) keyMap.set(oldKey, key);
  }
  return { plan: kept, keyMap };
}

/** Each spec's scenario key, as the vendor bids grid and the Closed Won dialog key them: `aud{i}:{n}`
 *  on a Multi-Audience plan, else the country code and the spec's index across that country. */
export function specKeys(plan: SamplePlanCountry[]): Map<SamplePlanSpec, string> {
  const keys = new Map<SamplePlanSpec, string>();
  const isMultiAudience = plan.length === 1 && (plan[0]?.audiences.length ?? 0) > 1;
  for (const c of plan) {
    if (isMultiAudience) {
      c.audiences.forEach((a, ai) => a.specs.forEach((s, si) => keys.set(s, `aud${ai}:${si}`)));
    } else {
      c.audiences.flatMap(a => a.specs).forEach((s, si) => keys.set(s, `${c.country}:${si}`));
    }
  }
  return keys;
}

/** Splits `total` across `parts` in proportion, in whole numbers that add up to exactly `total`. */
export function splitInProportion(parts: number[], total: number): number[] {
  const sum = parts.reduce((s, p) => s + p, 0);
  if (sum <= 0) return parts.map(() => 0);
  const split = parts.map(p => Math.floor((p / sum) * total));
  // Hand the rounding remainder to the largest parts first.
  const order = parts.map((p, i) => i).sort((a, b) => parts[b] - parts[a]);
  for (let i = 0, left = total - split.reduce((s, p) => s + p, 0); left > 0; i = (i + 1) % order.length, left--) {
    split[order[i]]++;
  }
  return split;
}

/**
 * Divides a bid's switched-on services by the waves in a tracker year, through their quantities:
 * hours, fixed prices, custom amounts and selected vendor quotes. A Rate Card price is tier rate × LOI,
 * with no quantity to divide, so those are left alone and their names returned for Sales to check.
 */
export function divideServicesForWave(
  templates: TemplateRow[],
  state: BidServicesState | null,
  bids: BidServicesBidsState | null,
  waves: number,
): string[] {
  const skipped: string[] = [];
  if (!state) return skipped;
  // Legacy drafts can lack a slice entirely.
  state.svcHrs ??= {};
  state.svcFixed ??= {};
  state.svcAmt ??= {};
  if (bids) bids.svcQuotes ??= {};
  for (const tmpl of templates) {
    if (tmpl.name === 'Sample' || !state.svcOn?.[tmpl.id]) continue;
    if (tmpl.type === 'Labor') {
      state.svcHrs[tmpl.id] = waveShare(state.svcHrs[tmpl.id] ?? tmpl.hours ?? 0, waves);
    } else if (tmpl.type === 'Fixed') {
      state.svcFixed[tmpl.id] = waveShare(state.svcFixed[tmpl.id] ?? state.svcFixedRate?.[tmpl.id] ?? tmpl.fixedRate ?? 0, waves);
    } else if (tmpl.type === 'Custom') {
      state.svcAmt[tmpl.id] = String(waveShare(parseFloat(state.svcAmt[tmpl.id] ?? '') || 0, waves));
    } else if (tmpl.type === 'Vendor' && bids) {
      const quotes = bids.svcQuotes[tmpl.id] ?? {};
      for (const vendorId of bids.svcSelected?.[tmpl.id] ?? []) {
        quotes[vendorId] = String(waveShare(parseFloat(quotes[vendorId] ?? '') || 0, waves));
      }
      bids.svcQuotes[tmpl.id] = quotes;
    } else if (tmpl.type === 'Rate Card') {
      skipped.push(tmpl.name);
    }
  }
  return skipped;
}
